/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import React, { Fragment } from "react";
import Button from "@react/react-spectrum/Button";
import SearchInputUICss from "./SearchInputUI.css";
import CloseIcon from "dc-icons/common/s_uidialogclose_12_N.svg";
import CloseIconModern from "dc-icons/viewer2021/S_CrossLarge_18_N.svg";
import CloseIconModernMobile from "dc-icons/viewer2021/Web_Mobile/S_CrossLarge_22_N.svg";
import { getIsInPlaceMode } from "selectors/viewModeSelectors";
import { isSinglePageMode } from "selectors/documentSelectors";
import { FormattedMessage } from "react-intl";
import { isCurrentDeviceMobile } from "dc-preview-utils/src/Utils/DeviceUtils";
import { isIntegrationContext } from "utils/common";
import SearchInputDarkThemeUI from "./SearchInputDarkThemeUI.css";
import * as stores from "stores";
import ButtonWrapper from "dc-preview-utils/src/components/Button/ButtonWrapper";

class CancelSearchButton extends React.Component {
  getButtonStyle() {
    let cancelButtonStyle = {};
    if (isIntegrationContext(this.props.appContext)) {
      cancelButtonStyle.padding = this.props.useModernViewer
        ? getIsInPlaceMode(stores) || isSinglePageMode(stores)
          ? "0px"
          : "0px 8px 0px 0px"
        : "2px 16px";
      cancelButtonStyle.marginTop = this.props.useModernViewer ? "1px" : "0px";
    } else {
      cancelButtonStyle.padding = "2px 6px";
    }
    return cancelButtonStyle;
  }

  getCancelSearchButton(label) {
    let cancelButton = null;
    let cancelButtonClass = null;
    if (this.props.color === "dark") {
      if (!getIsInPlaceMode(stores)) {
        cancelButtonClass = SearchInputDarkThemeUI.CancelButton;
      }
    }

    if (this.props.useMobileModernViewer) {
      cancelButton = <ButtonWrapper icon={CloseIconModernMobile} clickHandler={this.props.onClick} {...this.props} />;
    } else if (this.props.useModernViewer) {
      cancelButton = <ButtonWrapper icon={CloseIconModern} clickHandler={this.props.onClick} {...this.props} />;
    } else {
      cancelButton = (
        <Button label={label} onClick={this.props.onClick} className={cancelButtonClass} variant="primary" />
      );
    }
    return cancelButton;
  }

  componentDidMount() {
    if (this.props.useModernViewer) {
      let closeIconSvgFillColor = "var(--spectrum-global-color-gray-700)";
      this.setSvgStyle(closeIconSvgFillColor);
    }
  }

  setSvgStyle(fillColor) {
    const svg = this.innerRef.getElementsByTagName("svg");
    const paths = this.innerRef.getElementsByTagName("path");
    if (svg.length) {
      svg[0].style.fill = fillColor;
    }
    Array.from(paths).forEach(elem => {
      const element = elem;
      element.style.fill = fillColor;
    });
  }

  render() {
    let cancelButtonStyle = this.getButtonStyle();
    let closeIconClass = getIsInPlaceMode(stores) ? SearchInputUICss.CloseButtonInplace : SearchInputUICss.CloseIcon;
    const showCancelSearchBtn = this.props.useModernViewer || (!getIsInPlaceMode(stores) && !isCurrentDeviceMobile());

    return (
      <Fragment>
        {showCancelSearchBtn ? (
          <FormattedMessage id="common.cancel">
            {label => (
              <div
                ref={innerRef => {
                  this.innerRef = innerRef;
                }}
                className={SearchInputUICss.CancelButton}
                style={cancelButtonStyle}
                data-testid="CancelSearchButton" // For test automation
              >
                {this.getCancelSearchButton(label)}
              </div>
            )}
          </FormattedMessage>
        ) : (
          <div className={closeIconClass} onClick={this.props.onClick}>
            <CloseIcon />
          </div>
        )}
      </Fragment>
    );
  }
}

export default CancelSearchButton;
